import { useModal } from "@ebay/nice-modal-react";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { getLocalTimeZone } from "@internationalized/date";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Datepicker from "ds/components/Datepicker";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import Feedback from "ds/components/Feedback";
import FormField from "ds/components/Form/Field";
import SegmentedControlFormField from "ds/components/SegmentedControlFormField";
import Tile from "ds/components/Tile";
import { TimezoneDisplay } from "shared/Stack/Scheduling/types";
import { ScheduledDelete } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { getNowDateTime } from "utils/date";
import { validateDateIsAfterNow } from "utils/formValidators";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";

import { getFormDefaultValues, mapCreateFormData } from "./helpers";
import { StackManageScheduledDeleteFormFields } from "./types";
import useCreateScheduledDelete from "./useCreateScheduledDelete";
import useUpdateScheduledDelete from "./useUpdateScheduledDelete";

type StackManageScheduledDeleteProps = {
  integration?: ScheduledDelete;
  stackId: string;
  setIsDirty: (isDirty: boolean) => void;
};

const StackManageScheduledDelete = ({
  integration,
  stackId,
  setIsDirty,
}: StackManageScheduledDeleteProps) => {
  const isEditMode = !!integration;
  const drawer = useModal();
  const { reportError } = useTypedContext(FlashContext);

  const localTimezone = getLocalTimeZone();

  const getMinDate = useCallback(
    (timezone: TimezoneDisplay) =>
      getNowDateTime({ timezone: timezone === "local" ? localTimezone : "UTC" }),
    [localTimezone]
  );

  const defaultDateTime = useMemo(
    () => getNowDateTime({ timezone: "UTC", timeShiftMinutes: 15 }),
    []
  );

  const scheduleStackDeletionForm = useForm<StackManageScheduledDeleteFormFields>({
    defaultValues: getFormDefaultValues(defaultDateTime, integration),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = scheduleStackDeletionForm;

  const timezoneDisplay = watch("timezoneDisplay");

  const { createScheduledDelete, loading: creationLoading } = useCreateScheduledDelete({ stackId });
  const { updateScheduledDelete, loading: updateLoading } = useUpdateScheduledDelete({ stackId });

  const handlePrimaryAction = useCallback(
    (formData: StackManageScheduledDeleteFormFields) => {
      const mappedData = mapCreateFormData(formData);

      if (!mappedData) {
        return reportError({ message: "We couldn't parse proper date" });
      }

      const onSuccess = () => {
        drawer.hide();
      };

      if (isEditMode) {
        updateScheduledDelete(integration.id, mappedData, onSuccess);
      } else {
        createScheduledDelete(mappedData, onSuccess);
      }
    },
    [isEditMode, integration?.id, drawer, updateScheduledDelete, createScheduledDelete, reportError]
  );

  const timezoneSegments = useMemo(
    () => [
      { value: "local", label: "Local timezone" },
      { value: "utc", label: "UTC" },
    ],
    []
  );

  const isSubmitting = creationLoading || updateLoading;
  const submitButtonText = isEditMode ? "Save" : "Create";

  useEffect(() => {
    setIsDirty(isDirty);
  }, [setIsDirty, isDirty]);

  return (
    <form onSubmit={handleSubmit(handlePrimaryAction)} {...getDrawerFormFix()}>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle
          title={isEditMode ? "Edit stack deletion schedule" : "Create stack deletion schedule"}
        />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody gap="x-large">
        <Feedback type="banner" variant="info">
          Stack deletion schedule only works on private workers.
        </Feedback>

        <Controller
          name="timezoneDisplay"
          control={control}
          render={({ field }) => (
            <SegmentedControlFormField
              value={field.value}
              segments={timezoneSegments}
              onChange={field.onChange}
              label=""
              noMargin
            >
              <Controller
                name="dateTime"
                control={control}
                rules={{
                  required: true,
                  validate: validateDateIsAfterNow,
                }}
                render={({ field: dateField, fieldState }) => (
                  <FormField label="Date and time" error={fieldState.error?.message} noMargin>
                    <Datepicker
                      withTime
                      renderTimezone={timezoneDisplay === "local" ? localTimezone : "UTC"}
                      date={dateField.value}
                      onChange={dateField.onChange}
                      minDate={getMinDate(timezoneDisplay)}
                      hasError={!!fieldState.error?.message}
                      fullWidth
                    />
                  </FormField>
                )}
              />
            </SegmentedControlFormField>
          )}
        />

        <Controller
          name="shouldDeleteResources"
          control={control}
          render={({ field }) => (
            <Box gap="x-large">
              <Tile
                title="Delete resources"
                indicator="radio"
                description="All resources that belong to this stack will be removed with it."
                selected={field.value}
                onClick={() => field.onChange(true)}
              />

              <Tile
                title="Keep resources"
                indicator="radio"
                description="Resources that belong to this stack will remain available when the stack is removed."
                selected={!field.value}
                onClick={() => field.onChange(false)}
              />
            </Box>
          )}
        />

        <DrawerFooter>
          <DrawerFooterActions>
            <DrawerCancelButton />

            <Button variant="primary" type="submit" loading={isSubmitting}>
              {submitButtonText}
            </Button>
          </DrawerFooterActions>
        </DrawerFooter>
      </DrawerBody>
    </form>
  );
};

export default StackManageScheduledDelete;
